(() => {
  const BASE = "https://cdn.shopify.com/s/files/1/0959/0531/7129/files/";
  const SKU_INDEX_URL = BASE + "sku-index.json";
  const STORAGE_KEY = "ridesrus_vehicle_v1";

  // --------- Normalisation ---------
  function normSku(s) {
    return String(s || "").trim().replace(/\s+/g, "").toUpperCase();
  }
  function normHandle(h) {
    return String(h || "").trim().toLowerCase();
  }
  function slug(s) {
    return (s || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function loadVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  function clearVehicle() {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("vehicle:changed", { detail: null }));
  }
  function formatVehicle(v) {
    if (!v) return "";
    const bits = [];
    if (v.year && v.year !== "0") bits.push(v.year);
    if (v.make) bits.push(v.make);
    if (v.model) bits.push(v.model);
    if (v.cc && v.cc !== "_any") bits.push(`${v.cc}cc`);
    return bits.join(" ");
  }

  // --------- Fetch + cache ---------
  async function fetchJsonCached(url, cacheKey, maxAgeMs) {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.t && (Date.now() - parsed.t) < maxAgeMs && parsed?.v) return parsed.v;
      }
    } catch {}

    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Fetch failed (${r.status}) ${url}`);
    const v = await r.json();

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), v }));
    } catch {}

    return v;
  }

  // Shopify collection products feed (pull all products without pagination UI)
  async function fetchAllCollectionProducts(collectionHandle) {
    const limit = 250; // Shopify public endpoint supports up to 250
    const url = `/collections/${collectionHandle}/products.json?limit=${limit}`;
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`Collection products fetch failed (${r.status}) ${url}`);
    const data = await r.json();
    return Array.isArray(data?.products) ? data.products : [];
  }

  // --------- Fitment logic ---------
  function collectSkus(makeData, model, cc, year) {
    const modelNode = makeData?.models?.[model];
    if (!modelNode) return [];

    const ccNode = modelNode?.cc || {};
    const ccCandidates = [];
    if (cc && ccNode[cc]) ccCandidates.push(cc);
    if (ccNode["_any"]) ccCandidates.push("_any");

    const yearCandidates = [];
    if (year && year !== "0") yearCandidates.push(String(year));
    yearCandidates.push("0"); // all-years bucket

    const out = new Set();
    for (const c of ccCandidates) {
      const yNode = ccNode[c] || {};
      for (const y of yearCandidates) {
        const payload = yNode[y];
        if (payload?.skus && Array.isArray(payload.skus)) {
          payload.skus.forEach(s => out.add(normSku(s)));
        }
      }
    }
    return Array.from(out);
  }

  // --------- DOM ---------
  function isCollectionPage() {
    return document.body.classList.contains("template-collection") || window.location.pathname.includes("/collections/");
  }

  function getCollectionHandleFromUrl() {
    const m = window.location.pathname.match(/\/collections\/([^\/\?\#]+)/i);
    return m ? m[1] : "";
  }

  function hideThemeGridAndPagination() {
    const grid = document.querySelector("#ProductGridContainer") || document.querySelector(".product-grid-container");
    if (grid) grid.style.display = "none";

    // Dawn pagination container usually has this class
    const pagination =
      document.querySelector(".pagination-wrapper") ||
      document.querySelector("nav.pagination") ||
      document.querySelector("[role='navigation'].pagination");
    if (pagination) pagination.style.display = "none";
  }

  function ensureBannerAndGridHost() {
    let wrap = document.querySelector("[data-fitment-collection-wrap]");
    if (wrap) return wrap;

    const before = document.querySelector("#ProductGridContainer") || document.querySelector(".product-grid-container");
    const parent = before?.parentElement || document.querySelector("main");
    if (!parent) return null;

    wrap = document.createElement("div");
    wrap.setAttribute("data-fitment-collection-wrap", "1");
    wrap.style.width = "100%";
    wrap.style.maxWidth = "var(--page-width)";
    wrap.style.margin = "12px auto 18px auto";
    wrap.style.padding = "0 1.5rem";

    wrap.innerHTML = `
      <div data-fitment-banner style="
        padding:12px 14px;
        border:1px solid rgba(var(--color-foreground), .12);
        border-radius:12px;
        background:rgb(var(--color-background));
        margin-bottom:14px;
        display:none;
      ">
        <div style="display:flex;gap:12px;flex-wrap:wrap;align-items:center;justify-content:space-between;">
          <div style="display:flex;flex-direction:column;gap:4px;">
            <div style="font-weight:700;">Filtering by your bike</div>
            <div style="opacity:.85;">
              <span data-fitment-vehicle></span>
              <span style="opacity:.6;">·</span>
              <span data-fitment-count></span>
            </div>
          </div>
          <div style="display:flex;gap:10px;flex-wrap:wrap;">
            <a class="button button--secondary" data-fitment-change href="/" style="white-space:nowrap;">Change vehicle</a>
            <button class="button" data-fitment-clear type="button" style="white-space:nowrap;">Clear filter</button>
          </div>
        </div>
      </div>

      <div data-fitment-status style="opacity:.85;margin:8px 0 12px 0;"></div>

      <div data-fitment-grid class="fitment-grid"></div>

      <div data-fitment-none style="display:none;opacity:.85;margin-top:10px;">
        No matching products in this collection for your selection.
      </div>
    `;

    // Insert near the grid
    if (before && before.parentElement) before.parentElement.insertBefore(wrap, before);
    else parent.prepend(wrap);

    // Theme-like grid CSS
    const style = document.createElement("style");
    style.textContent = `
      .fitment-grid{
        display:grid;
        gap:14px;
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
      .fitment-card{
        border: 1px solid rgba(var(--color-foreground), .12);
        border-radius: 12px;
        overflow: hidden;
        background: rgb(var(--color-background));
        display: grid;
        grid-template-rows: auto 1fr;
        height: 100%;
      }
      .fitment-card__img{
        display:block;
        width:100%;
        aspect-ratio: 1 / 1;
        object-fit: cover;
        background: rgba(var(--color-foreground), .04);
      }
      .fitment-card__body{
        padding: 12px;
        display:grid;
        gap:6px;
      }
      .fitment-card__title{
        font-weight: 700;
        line-height: 1.2;
        color: rgb(var(--color-foreground));
        text-decoration:none;
      }
      .fitment-card__meta{ opacity:.78; font-size: .9em; }
      .fitment-card__price{ font-weight:700; }
      @media (max-width: 989px){
        .fitment-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
      }
      @media (max-width: 520px){
        .fitment-grid{ grid-template-columns: 1fr; }
      }
    `;
    document.head.appendChild(style);

    // Change vehicle ALWAYS goes to homepage (force, no referrer logic)
    const change = wrap.querySelector("[data-fitment-change]");
        if (change) {
            const home = "/"; // homepage
            change.href = home;
            change.addEventListener("click", (e) => {
            e.preventDefault();
            window.location.assign(home);
         });
    }



    // Clear
    wrap.querySelector("[data-fitment-clear]").addEventListener("click", () => {
      clearVehicle();
      window.location.reload();
    });

    return wrap;
  }

  function buildCard(product, preferredSkuNorm) {
    const handle = normHandle(product.handle);
    const title = product.title || handle;
    const vendor = product.vendor || "";
    const img = (product.images && product.images[0] && product.images[0].src) ? product.images[0].src : "";

    // Find variant by SKU for deep link
    let variant = null;
    if (preferredSkuNorm && Array.isArray(product.variants)) {
      variant = product.variants.find(v => normSku(v.sku) === preferredSkuNorm) || null;
    }
    if (!variant && Array.isArray(product.variants)) variant = product.variants[0] || null;

    const url = variant?.id ? `/products/${handle}?variant=${variant.id}` : `/products/${handle}`;

    // price from products.json is a string like "69.99"
    const price = variant?.price ? `£${Number(variant.price).toFixed(2)}` : "";

    const card = document.createElement("div");
    card.className = "fitment-card";

    const aImg = document.createElement("a");
    aImg.href = url;
    aImg.setAttribute("aria-label", title);

    const im = document.createElement("img");
    im.className = "fitment-card__img";
    im.loading = "lazy";
    im.src = img;
    im.alt = title;

    aImg.appendChild(im);

    const body = document.createElement("div");
    body.className = "fitment-card__body";

    const aTitle = document.createElement("a");
    aTitle.className = "fitment-card__title";
    aTitle.href = url;
    aTitle.textContent = title;

    const meta = document.createElement("div");
    meta.className = "fitment-card__meta";
    meta.textContent = vendor || " ";

    const priceEl = document.createElement("div");
    priceEl.className = "fitment-card__price";
    priceEl.textContent = price;

    body.appendChild(aTitle);
    body.appendChild(meta);
    body.appendChild(priceEl);

    card.appendChild(aImg);
    card.appendChild(body);

    return card;
  }

  async function run() {
    if (!isCollectionPage()) return;

    const vehicle = loadVehicle();
    if (!vehicle?.make || !vehicle?.model) return; // no vehicle => leave collection normal

    const collectionHandle = getCollectionHandleFromUrl();
    if (!collectionHandle) return;

    const host = ensureBannerAndGridHost();
    if (!host) return;

    const banner = host.querySelector("[data-fitment-banner]");
    const statusEl = host.querySelector("[data-fitment-status]");
    const gridEl = host.querySelector("[data-fitment-grid]");
    const noneEl = host.querySelector("[data-fitment-none]");

    statusEl.textContent = "Loading compatible products…";

    const makeFile = BASE + slug(vehicle.make) + ".json";

    const [skuIndexRaw, makeData, collectionProducts] = await Promise.all([
      fetchJsonCached(SKU_INDEX_URL, "fitment_sku_index_v3", 60 * 60 * 1000),
      fetchJsonCached(makeFile, "fitment_make_" + slug(vehicle.make) + "_v3", 20 * 60 * 1000),
      fetchAllCollectionProducts(collectionHandle)
    ]);

    // normalize sku-index: normSku => handle
    const skuToHandle = {};
    for (const [sku, handle] of Object.entries(skuIndexRaw || {})) {
      const ns = normSku(sku);
      if (!ns) continue;
      skuToHandle[ns] = normHandle(handle);
    }

    // Fitment skus => handle + preferred sku (so we can deep-link variant)
    const fitmentSkus = collectSkus(makeData, vehicle.model, vehicle.cc || "_any", vehicle.year || "0");

    const handleToPreferredSku = {};
    for (const s of fitmentSkus) {
      const h = skuToHandle[s];
      if (!h) continue;
      // store first sku we see for that handle (good enough for Phase 1)
      if (!handleToPreferredSku[h]) handleToPreferredSku[h] = s;
    }

    // Now filter to only products in THIS collection
    const collectionByHandle = new Map();
    for (const p of (collectionProducts || [])) {
      collectionByHandle.set(normHandle(p.handle), p);
    }

    const matchingHandles = Object.keys(handleToPreferredSku).filter(h => collectionByHandle.has(h));

    // Hide the theme grid & pagination and show our filtered grid instead
    hideThemeGridAndPagination();

    banner.style.display = "block";
    host.querySelector("[data-fitment-vehicle]").textContent = formatVehicle(vehicle);

    gridEl.innerHTML = "";
    noneEl.style.display = "none";

    if (!matchingHandles.length) {
      statusEl.textContent = "";
      host.querySelector("[data-fitment-count]").textContent = `0 products`;
      noneEl.style.display = "block";
      return;
    }

    // Render cards
    matchingHandles.sort();
    for (const h of matchingHandles) {
      const p = collectionByHandle.get(h);
      if (!p) continue;
      gridEl.appendChild(buildCard(p, handleToPreferredSku[h]));
    }

    statusEl.textContent = "";
    host.querySelector("[data-fitment-count]").textContent = `${matchingHandles.length} products`;
  }

  document.addEventListener("DOMContentLoaded", () => {
    run().catch(e => console.error("Fitment collection filter error:", e));
    window.addEventListener("vehicle:changed", () => window.location.reload());
  });
})();
